import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { themeColours, type ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import { defaultStyleFor, WaveformShape } from 'fym-shared/src/music24/assetStyles';
import { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../common/types';
import { ThemeStill } from '../themes/ThemeStill';
import { ThemeStillLayout, themeStillVars } from '../themes/themeStillVars';
import { LoopStyleId, StyleLoopPreview } from '../themes/StyleLoopPreview';
import type { ThemeStillSong } from '../releases/theme/useThemeStillSong';
import { AssetFrame, isFrameAssetType } from './frames/AssetFrame';
import type { FrameLyric } from './frames/assetFrameVars';
import { StoryPreview } from './frames/StoryPreview';
import { studioStory, studioStoryStyle, utcToday } from './frames/studioStory';
import { studioMessages } from './studioMessages';
import { SpotifyHeaderFrame } from './spotifyHeader/SpotifyHeaderFrame';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The theme of the song as the step 2 pictures need it. */
export interface AssetPreviewTheme {
  look: ReleaseKitLook;
  palette: readonly string[];
  song: ThemeStillSong;
  /** The sung line of the lyric frames (design s7). Absent: the title. */
  lyric?: FrameLyric;
}

/** The day count of the countdown card (design q3a: "3 days"). */
const COUNTDOWN_DAYS = 3;

/**
 * The picture kind of each asset type: a drawn still (images), a drawn loop (videos), the cover,
 * the drawn lyric card, or a drawn frame of design s (the full videos, the lyric moment, the
 * motion artwork).
 */
export type PreviewKind =
  | { kind: 'still'; tall: boolean }
  | { kind: 'loop'; style: LoopStyleId; waveform?: WaveformShape; text?: 'countdown' }
  | { kind: 'cover' }
  | { kind: 'lyric' }
  | { kind: 'frame' }
  | { kind: 'header' };

/**
 * The picture of each asset type in step 2 (design q3a: `a1` AI footage, `a2` the loop style of
 * the theme, `a3` type only with "3 days", `a4` liquid). The design draws only the four short
 * videos; the other asset types get the picture that their file has.
 */
const PREVIEWS: Readonly<Record<StudioThemeId, PreviewKind>> = {
  story: { kind: 'still', tall: true },
  feedPost: { kind: 'still', tall: false },
  banners: { kind: 'still', tall: false },
  spotifyHeader: { kind: 'header' },
  thumbnail: { kind: 'still', tall: false },
  coverSizes: { kind: 'cover' },
  lyricCard: { kind: 'lyric' },
  linkPreview: { kind: 'still', tall: false },
  aiReel: { kind: 'loop', style: 'aiFootage' },
  artworkInMotion: { kind: 'loop', style: 'drift' },
  countdown: { kind: 'loop', style: 'typeOnly', text: 'countdown' },
  canvasLoop: { kind: 'loop', style: 'liquid' },
  lyricMoment: { kind: 'frame' },
  motionArtwork: { kind: 'frame' },
  visualizer: { kind: 'frame' },
  lyricVideo: { kind: 'frame' },
  musicVideo: { kind: 'frame' },
};

/** The width in px of the 7:3 Spotify header picture of each place. */
const HEADER_PX: Readonly<Record<AssetPreviewSize, number>> = { card: 180, thumb: 40, slot: 240 };

/** The width in px of the square lyric card picture of each place (the CSS height of `.assetPic`). */
const LYRIC_CARD_PX: Readonly<Record<AssetPreviewSize, number>> = { card: 88, thumb: 36, slot: 136 };

const STILL_LAYOUTS: Readonly<Record<string, ThemeStillLayout>> = {
  coverCard: 'card',
  coverGround: 'ground',
  typeOnly: 'type',
};

const LOOP_STYLES: ReadonlySet<string> = new Set(['drift', 'pulse', 'liquid', 'waveform', 'particles']);

/** The picture kind of an asset type in a theme: Artwork in motion takes the loop style of the theme. Pure. */
export function assetPreviewKind(assetTypeId: StudioThemeId, look: Pick<ReleaseKitLook, 'themeId'>): PreviewKind {
  const preview = PREVIEWS[assetTypeId];
  if (assetTypeId === 'artworkInMotion') {
    const choice = defaultStyleFor(look.themeId, assetTypeId);
    // A theme whose default is AI footage shows a cover drift here (design q3a, `a2`).
    if (choice === null || !LOOP_STYLES.has(choice.style)) {
      return preview;
    }
    return choice.waveform === undefined
      ? { kind: 'loop', style: choice.style as LoopStyleId }
      : { kind: 'loop', style: choice.style as LoopStyleId, waveform: choice.waveform };
  }
  return preview;
}

/** The 9:16 pictures: the story, the loops and the lyric moment. Pure. */
export function isTallPreview(assetTypeId: StudioThemeId, preview: PreviewKind): boolean {
  if (preview.kind === 'still') {
    return preview.tall;
  }
  return preview.kind === 'loop' || assetTypeId === 'lyricMoment';
}

/** Where the picture shows: see `AssetTypePreviewProps.size`. */
export type AssetPreviewSize = 'card' | 'thumb' | 'slot';

const WRAPPER_CLASS: Readonly<Record<AssetPreviewSize, string | undefined>> = {
  card: styles.assetPicFrame,
  thumb: styles.assetPicThumb,
  slot: styles.assetPicSlot,
};

interface AssetTypePreviewProps {
  assetTypeId: StudioThemeId;
  theme: AssetPreviewTheme;
  /**
   * `card` (default): the step 2 card, a box of a fixed height. `thumb`: the small picture of a
   * Work row, a fixed width; every picture shows whole at its own aspect (a 9:16 one is narrower).
   * `slot`: the thumb of an asset card on the song page that has no file yet; it fills the height
   * of the thumb box.
   */
  size?: AssetPreviewSize;
}

/**
 * The small picture of an asset type in step 2 (design q3a): drawn in the browser in the song's
 * theme colours and type, with the song's cover (`themeColours`, `themeType`). It makes no render
 * call. The lyric card is the new square card, drawn with the layout of the render.
 */
const AssetTypePreviewComponent: React.FC<AssetTypePreviewProps> = ({ assetTypeId, theme, size = 'card' }) => {
  const intl = useIntl();
  const { look, palette, song, lyric } = theme;
  const preview = assetPreviewKind(assetTypeId, look);
  const isThumb = size === 'thumb';
  const picClass = clsx(styles.assetPic, isThumb && isTallPreview(assetTypeId, preview) && styles.assetPicTall);

  const picture = ((): React.ReactNode => {
    switch (preview.kind) {
      case 'still': {
        const style = defaultStyleFor(look.themeId, assetTypeId)?.style ?? 'coverCard';
        return (
          <ThemeStill
            themeId={look.themeId}
            fontOverride={look.fontOverride}
            palette={palette}
            artworkUrl={song.artworkUrl}
            title={song.title}
            artist={song.artist}
            kicker={song.kicker}
            layout={STILL_LAYOUTS[style] ?? 'card'}
            tall={preview.tall}
            className={picClass}
          />
        );
      }
      case 'loop':
        return (
          <StyleLoopPreview
            style={preview.style}
            waveform={preview.style === 'waveform' ? (preview.waveform ?? 'bars') : null}
            themeId={look.themeId}
            fontOverride={look.fontOverride}
            palette={palette}
            artworkUrl={song.artworkUrl}
            playing={false}
            text={
              preview.text === 'countdown'
                ? intl.formatMessage(studioMessages.countdownDays, { count: COUNTDOWN_DAYS })
                : undefined
            }
            className={picClass}
          />
        );
      case 'lyric': {
        // The new lyric card (design `asset-redesign/k`): the lyric Story in a square, drawn with the
        // layout of the render, in the song's theme. The song has no picked line here, so the card
        // quotes the sung line of the frames. A song with no lyrics shows its title: the card of one
        // song never quotes another song (QA 2026-10-24 B17).
        const style = defaultStyleFor(look.themeId, 'lyricCard')?.style;
        const story = studioStory({
          format: 'lyric11',
          moment: 'lyricLine',
          lyricLine: lyric?.current,
          title: song.title,
          artist: song.artist,
          releaseDate: null,
          lyrics: null,
          link: null,
          withLogo: false,
          today: utcToday(),
        });
        return (
          <StoryPreview
            story={story}
            look={look}
            palette={palette}
            storyStyle={studioStoryStyle(style)}
            artworkUrl={song.artworkUrl}
            logoUrl={null}
            width={LYRIC_CARD_PX[size]}
            className={picClass}
          />
        );
      }
      case 'frame':
        return isFrameAssetType(assetTypeId) ? (
          <AssetFrame
            assetTypeId={assetTypeId}
            look={look}
            palette={palette}
            song={song}
            lyric={lyric}
            fixedHeight={!isThumb}
            className={picClass}
          />
        ) : null;
      case 'header':
        return (
          <SpotifyHeaderFrame
            width={HEADER_PX[size]}
            view="file"
            ground={{ kind: 'cover', artworkUrl: song.artworkUrl }}
            colours={themeColours(look.themeId, palette)}
            className={picClass}
          />
        );
      case 'cover':
        return (
          <span
            className={clsx(picClass, styles.assetCover)}
            style={themeStillVars({
              themeId: look.themeId,
              fontOverride: look.fontOverride,
              palette,
              artworkUrl: song.artworkUrl,
              title: '',
              layout: 'card',
            })}
          />
        );
    }
  })();

  return (
    <span
      className={WRAPPER_CLASS[size]}
      data-testid={`studio-asset-preview-${assetTypeId}`}
      data-preview={preview.kind}
    >
      {picture}
    </span>
  );
};

export const AssetTypePreview = TypedMemo(AssetTypePreviewComponent);
